import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

/* ===== CryptoTradeMode — the crypto PAPER/LIVE switch (Shark webhook) =====
   Mirror of the Indian TradeMode: the flip changes ROUTING ONLY — same books,
   same signals, same management; LIVE means each action's webhook intent is
   actually POSTed to Shark (at liveQtyFrac of paper size) instead of logged.
   Defense in depth: the switch alone sends nothing — SHARK_WEBHOOK_URL,
   SHARK_WEBHOOK_UUID and SHARK_WEBHOOK_LIVE=1 must ALSO be set in server/.env
   (the user arms those by hand on the day). Going LIVE requires typing LIVE.
   The user's own rule: no live flip before 200 more closed validation trades
   (counter shown — advisory, the day is the user's call). */

export default function CryptoTradeMode() {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pipe, setPipe] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get("/api/crypto/trade-mode", { timeout: 10000 });
      if (r.data.success) setSt(r.data);
    } catch {}
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  const setMode = async (mode) => {
    setBusy(true); setMsg("");
    try {
      const body = mode === "live" ? { mode, confirm: confirmText } : { mode };
      const r = await axios.post("/api/crypto/trade-mode", body);
      if (r.data.success) { setMsg(`Mode: ${r.data.mode.toUpperCase()}`); setConfirmText(""); load(); }
      else setMsg(r.data.message || "failed");
    } catch (e) { setMsg(e.response?.data?.message || e.message); }
    setBusy(false);
  };

  const runPipeTest = async () => {
    setBusy(true); setPipe({ running: true });
    try {
      const r = await axios.post("/api/crypto/pipe-test", {}, { timeout: 30000 });
      setPipe(r.data);
    } catch (e) { setPipe({ steps: [{ step: "request", ok: false, detail: e.message }] }); }
    setBusy(false);
  };

  if (!st) return null;
  const live = st.mode === "live";
  const armed = st.urlConfigured && st.uuidConfigured && st.envArmed;
  const hot = live && armed;   // orders would actually POST
  const v = st.validation || {};
  const Gate = ({ ok, label }) => (
    <span style={{ marginRight: 10 }}>
      <span className={ok ? "positive" : "negative"}>{ok ? "✓" : "✗"}</span> {label}
    </span>
  );

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} title="Crypto trade mode (Shark webhook) — click to manage"
        style={{
          fontWeight: 800, fontSize: 12, padding: "4px 14px", borderRadius: 14, cursor: "pointer",
          background: hot ? "#dc2626" : live ? "#b45309" : "var(--btn-muted)",
          color: live ? "#fff" : "var(--grey-light)",
          border: hot ? "1px solid #b91c1c" : "1px solid var(--btn-border)",
          animation: hot ? "pulse 2s infinite" : undefined,
        }}>
        {hot ? "🔴 LIVE" : live ? "🟠 LIVE (gates off)" : "📄 PAPER"}
      </button>

      {open && (
        // fixed to the viewport (not the pill): the Crypto tab is full-bleed,
        // so an absolutely-anchored dropdown can land off-screen
        <div style={{ position: "fixed", top: 84, right: 16, zIndex: 900, width: "min(400px, 94vw)", maxHeight: "80vh", overflowY: "auto", background: "var(--panel2, #1b1f27)", border: "1px solid var(--panel3)", borderRadius: 10, padding: 14, boxShadow: "0 8px 30px rgba(0,0,0,.4)" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Crypto mode: {hot ? "🔴 LIVE — real Shark orders" : live ? "🟠 switch LIVE but env gates off — still dry-run" : "📄 PAPER — intents logged, nothing sent"}
          </div>

          <div style={{ fontSize: 11.5, color: "var(--muted2)", lineHeight: 1.8, marginBottom: 8 }}>
            <Gate ok={st.urlConfigured} label="webhook URL in .env" />
            <Gate ok={st.uuidConfigured} label="UUID in .env" /><br />
            <Gate ok={st.envArmed} label="SHARK_WEBHOOK_LIVE=1" />
            <Gate ok={st.cancelVerified} label="cancel template verified" /><br />
            live size: <b>{Math.round((st.liveQtyFrac || 0) * 100)}%</b> of paper qty (config liveQtyFrac)
            {" · "}validation: <b className={v.done >= v.target ? "positive" : ""}>{v.done ?? 0} / {v.target ?? 200}</b> closed trades since {v.since}
          </div>
          {!st.cancelVerified && <div style={{ fontSize: 10.5, color: "var(--warn-text)", marginBottom: 8 }}>
            ⚠️ Stop-moves need cancel+replace: until the CANCEL template is verified from the Shark drawer, trails/BE moves stay blocked live. Grab the Cancel Open Order JSON from Shark and we verify it.
          </div>}

          {live ? (
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => setMode("paper")}
              style={{ width: "100%", marginBottom: 8 }}>Switch to 📄 PAPER (immediate)</button>
          ) : (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "var(--muted2)", marginBottom: 4 }}>
                To flip the switch, type <b>LIVE</b>. Routing only — same books, same signals; orders go to Shark at {Math.round((st.liveQtyFrac || 0) * 100)}% size. Nothing sends unless the .env gates are also armed (your job, on the day).
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="ema-select" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  placeholder="type LIVE" style={{ width: 110 }} />
                <button className="btn btn-sm btn-danger" disabled={busy || confirmText !== "LIVE"}
                  onClick={() => setMode("live")}>Flip to LIVE</button>
              </div>
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--panel3)", paddingTop: 8, marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="btn btn-sm btn-secondary" disabled={busy} onClick={runPipeTest}>🧪 Test order: place + cancel</button>
              <span style={{ fontSize: 10, color: "var(--muted2)" }}>
                smallest legal BTC lot, LIMIT parked ~10% below market (cannot fill), then CANCEL — dry-run logs intents; env-armed = real order at Shark, verify in the app. Fill tests (market buy + close) stay manual by design: run them yourself via curl/app.
              </span>
            </div>
            {pipe && (
              <div style={{ marginTop: 8, fontSize: 11 }}>
                {pipe.running ? "running…" : (pipe.steps || []).map((s, i) => (
                  <div key={i} style={{ color: s.ok ? "var(--muted2)" : "var(--pale-neg, #f87171)" }}>
                    {s.ok ? "✓" : "✗"} {s.step}: {s.detail}
                  </div>
                ))}
              </div>
            )}
          </div>
          {msg && <div style={{ marginTop: 8, fontSize: 11 }} className="positive">{msg}</div>}
        </div>
      )}
    </div>
  );
}
